/* eslint-disable @typescript-eslint/no-floating-promises */

import SessionManager from "@Core/session";
import localforage from "localforage";
import { extendPrototype } from "localforage-observable";
import { useEffect, useMemo, useReducer, useRef, useState } from "react";
import { Observable } from "rxjs";

type ValueWithExpiry<T> = {
    value: T;
    expiry?: Date;
};
type ISetItemAction<T> = T | ((currentValue: T, expiry: number) => T);

export type ILocalStorage<T> = [
    ValueWithExpiry<T>,
    (newValue: ISetItemAction<T>, expiry?: number) => void,
    (key: string) => Promise<void>
];
export function useLocalStorage<T>(key: string, defaultValue?: ValueWithExpiry<T>): ILocalStorage<T> {
    const isFirstRenderRef = useRef(true);
    const [value, setValue] = useState<ValueWithExpiry<T> | undefined>(defaultValue);
    const [id, forceUpdate] = useReducer((num: number) => num + 1, 0);

    const localForage = extendPrototype(localforage);

    const userStorage = localForage.createInstance({
        name: SessionManager.getServiceUser()?.userId,
    });

    let subscription: Subscription | null = null;

    userStorage.ready(() => {
        if (isFirstRenderRef.current === false) return;

        userStorage.newObservable.factory = (subscribeFn) => {
            return new Observable(subscribeFn);
        };

        const observable = userStorage.newObservable({
            crossTabChangeDetection: true,
            crossTabNotification: true,
            key,
        });

        subscription = observable.subscribe({
            next: ({ key, newValue, valueChange: hasChanged, ..._args }) => {
                if (hasChanged) {
                    setValue(newValue);
                    forceUpdate();
                }
            },
            error: (_err) => {
                // eslint-disable-next-line no-console
                console.error("unable to subscribe to changes in local storage");
            },
        });
        userStorage.getItem<ValueWithExpiry<T>>(key).then((storedValue) => {
            if (storedValue) {
                if (storedValue?.expiry instanceof Date && new Date().getTime() > storedValue?.expiry.getTime()) {
                    setValue(undefined);
                } else {
                    setValue(storedValue);
                }
            }
            forceUpdate();
            isFirstRenderRef.current = false;
        });
    });

    // clean up the subscription on unmount
    useEffect(() => {
        return () => {
            subscription?.unsubscribe();
        };
    }, [subscription]);

    /**
     *  Some changes won't trigger a rerender on their own.
     */
    const checkIfShouldForceUpdate = (rawValue: T): boolean =>
        !["string", "number", "boolean"].includes(typeof rawValue);

    const setItem = async (newValue: T, expiryInMinutes?: number) => {
        const rawValue = newValue instanceof Function ? newValue(value as T) : newValue;
        const now = new Date();
        const expiryDate = expiryInMinutes ? new Date(now.getTime() + 60000 * expiryInMinutes) : null;
        const itemWithExpiry = {
            value: rawValue,
            expiry: expiryDate,
        };
        await userStorage.setItem(key, itemWithExpiry).then(() => {
            const shouldForceUpdate = checkIfShouldForceUpdate(rawValue);
            setValue(itemWithExpiry);
            if (shouldForceUpdate) forceUpdate();
            forceUpdate();
        });
    };

    const getItem = async (key: string) => {
        await userStorage.getItem<ValueWithExpiry<T>>(key).then((storedValue) => {
            if (storedValue) {
                if (storedValue?.expiry instanceof Date && new Date().getTime() > storedValue?.expiry.getTime()) {
                    setValue(undefined);
                } else {
                    setValue(storedValue);
                }
            }

            forceUpdate();
        });
    };

    return useMemo(
        () => [value, setItem, getItem],

        // eslint-disable-next-line react-hooks/exhaustive-deps
        [id, key, value]
    );
}
